import { useEffect, useState } from "react";

const DISMISS_KEY = "hcob_worker_install_dismissed_at";
const DISMISS_DAYS = 14;

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone = "standalone" in window.navigator && window.navigator.standalone;
  return !!(mm || iosStandalone);
}

// Captures Chrome/Edge/Android's `beforeinstallprompt` event so the worker
// portal can show a big in-app "Install app" button instead of the browser's
// tiny address-bar hint. Also detects iOS Safari (no native prompt) so we
// can show an "Add to Home Screen" instructional card there.
export default function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [ios] = useState(isIos());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) {
      setDismissed(true);
    }
    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return { outcome: "unavailable" };
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return choice;
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  // Show the banner when:
  //  - Not already installed
  //  - Not recently dismissed
  //  - Either the native prompt is captured OR we're on iOS Safari (needs A2HS instructions)
  const canShow = !installed && !dismissed && (!!deferredPrompt || ios);

  return { canShow, install, dismiss, installed, ios, hasNativePrompt: !!deferredPrompt };
}
